import React, { useEffect, useMemo, useRef } from 'react';
import { h, C, n2, txt, fmtRpm } from './svgKit';

// To-scale front view of the planetary set: sun, N planets, ring (internal teeth) and the
// carrier spider joining the planet pins. All gears share one module, so each pitch radius
// is proportional to its tooth count. When `animate` is on, each member turns at a speed
// proportional to its rpm (the fastest member at ~0.4 rev/s), written straight to the
// transform attributes from a requestAnimationFrame loop so React does not re-render per
// frame. Positive speed = clockwise in this view.

const VB = 320;
const CX = VB / 2;
const CY = VB / 2;
const R_OUTER = 150;
const DISPLAY_MAX_DEG_S = 144;

function gearPath(z, rPitch, m, internal) {
  const p = (2 * Math.PI) / z;
  const rTip = internal ? rPitch - m : rPitch + m;
  const rRoot = internal ? rPitch + 1.25 * m : rPitch - 1.25 * m;
  const pts = [];
  for (let i = 0; i < z; i += 1) {
    const th = i * p - Math.PI / 2;
    pts.push([th - 0.27 * p, rRoot], [th - 0.15 * p, rTip], [th + 0.15 * p, rTip], [th + 0.27 * p, rRoot]);
  }
  return pts.map(([a, r], i) => `${i ? 'L' : 'M'}${n2(r * Math.cos(a))},${n2(r * Math.sin(a))}`).join(' ') + ' Z';
}

const circlePath = (r) => `M${-r},0 a${r},${r} 0 1,0 ${2 * r},0 a${r},${r} 0 1,0 ${-2 * r},0 Z`;

const PlanetaryCutaway = ({ gear, speeds, pinionRelRpm, animate, generalLabels }) => {
  const { zs, zr, zp, n } = gear;
  const geo = useMemo(() => {
    const rr = R_OUTER / (1 + 8.5 / zr);
    const m = (2 * rr) / zr;
    const rs = (zs * m) / 2;
    const rp = (zp * m) / 2;
    const a = (rs + rr) / 2;
    return {
      m, rs, rr, rp, a,
      sun: gearPath(zs, rs, m, false),
      planet: gearPath(zp, rp, m, false),
      ring: `${circlePath(R_OUTER)} ${gearPath(zr, rr, m, true)}`,
    };
  }, [zs, zr, zp]);

  const sunRef = useRef(null);
  const ringRef = useRef(null);
  const carrierRef = useRef(null);
  const planetRefs = useRef([]);
  const cLabelRef = useRef(null);
  const angles = useRef({ s: 0, c: 0, r: 0, p: 0 });

  const rates = useMemo(() => {
    const vals = [speeds.sunRpm, speeds.carrierRpm, speeds.ringRpm, pinionRelRpm];
    const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
    const k = DISPLAY_MAX_DEG_S / max;
    return { s: speeds.sunRpm * k, c: speeds.carrierRpm * k, r: speeds.ringRpm * k, p: pinionRelRpm * k };
  }, [speeds.sunRpm, speeds.carrierRpm, speeds.ringRpm, pinionRelRpm]);

  useEffect(() => {
    const apply = () => {
      const A = angles.current;
      sunRef.current?.setAttribute('transform', `translate(${CX} ${CY}) rotate(${n2(A.s)})`);
      ringRef.current?.setAttribute('transform', `translate(${CX} ${CY}) rotate(${n2(A.r)})`);
      carrierRef.current?.setAttribute('transform', `translate(${CX} ${CY}) rotate(${n2(A.c)})`);
      planetRefs.current.forEach((el) => el?.setAttribute('transform', `rotate(${n2(A.p)})`));
      if (cLabelRef.current) {
        // Midway between planet 0 and planet 1 on the carrier spider, kept upright.
        // (With 2 planets the spider is a straight bar through the centre: label it on the bar.)
        const mid = (A.c - 90 + (n === 2 ? 0 : 180 / n)) * (Math.PI / 180);
        const r = n === 2 ? (geo.rs + geo.a) / 2 + geo.m : Math.max(geo.a * Math.cos(Math.PI / n), geo.rs + 3 * geo.m);
        cLabelRef.current.setAttribute('x', n2(CX + r * Math.cos(mid)));
        cLabelRef.current.setAttribute('y', n2(CY + r * Math.sin(mid) + 4));
      }
    };
    apply();
    if (!animate) return undefined;
    let raf;
    let last = null;
    const tick = (t) => {
      if (last !== null) {
        const dt = Math.min(0.1, (t - last) / 1000);
        const A = angles.current;
        A.s = (A.s + rates.s * dt) % 360;
        A.c = (A.c + rates.c * dt) % 360;
        A.r = (A.r + rates.r * dt) % 360;
        A.p = (A.p + rates.p * dt) % 360;
        apply();
      }
      last = t;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, rates, geo, n]);

  const planets = [];
  for (let k = 0; k < n; k += 1) {
    const th = (2 * Math.PI * k) / n - Math.PI / 2;
    const px = geo.a * Math.cos(th);
    const py = geo.a * Math.sin(th);
    // Phase the planet so a tooth gap faces each sun tooth at the contact point (the sun's
    // tooth / gap position there, shifted half a pitch), so the teeth mesh instead of
    // overlapping. Gear paths have a tooth centred at -90 deg.
    const ps = (2 * Math.PI) / zs;
    const pp = (2 * Math.PI) / zp;
    const fSun = ((((th + Math.PI / 2) / ps) % 1) + 1) % 1;
    const phase = ((th + Math.PI + Math.PI / 2) - pp * (fSun + 0.5)) * (180 / Math.PI);
    planets.push(h('g', { key: `p${k}`, transform: `translate(${n2(px)} ${n2(py)})` },
      h('g', { ref: (el) => { planetRefs.current[k] = el; } },
        h('path', { d: geo.planet, transform: `rotate(${n2(phase)})`, style: { fill: C.mutedBg, stroke: C.muted, strokeWidth: 1 } }),
        h('line', { x1: 0, y1: -geo.rp * 0.55, x2: 0, y2: geo.rp * 0.55, style: { stroke: C.muted, strokeWidth: 1.2 } })),
      h('circle', { r: Math.max(3, geo.m * 1.3), style: { fill: C.carrier } }),
      k === 0 ? txt('pl', 0, -geo.rp * 0.62, 'P', { anchor: 'middle', size: 10, weight: 700, color: C.muted }) : null));
  }
  const spider = [];
  for (let k = 0; k < n; k += 1) {
    const th = (2 * Math.PI * k) / n - Math.PI / 2;
    spider.push(`${k ? 'L' : 'M'}${n2(geo.a * Math.cos(th))},${n2(geo.a * Math.sin(th))}`);
  }

  const svg = h('svg', {
    viewBox: `0 0 ${VB} ${VB}`, className: 'w-full h-auto max-w-[340px] mx-auto', role: 'img',
    'aria-label': `Planetary gear set: sun ${zs} teeth, ${n} planets of ${zp} teeth, ring ${zr} teeth`,
    'data-testid': 'ps-cutaway-svg',
  },
  h('g', { key: 'carrier', ref: carrierRef, transform: `translate(${CX} ${CY})` },
    h('path', { d: `${spider.join(' ')} Z`, style: { fill: 'none', stroke: C.carrier, strokeOpacity: 0.55, strokeWidth: Math.max(6, geo.m * 3), strokeLinejoin: 'round' } }),
    ...planets),
  h('g', { key: 'ring', ref: ringRef, transform: `translate(${CX} ${CY})` },
    h('path', { d: geo.ring, fillRule: 'evenodd', style: { fill: C.ring, fillOpacity: 0.28, stroke: C.ring, strokeWidth: 1.2 } }),
    h('line', { x1: 0, y1: -R_OUTER, x2: 0, y2: -(geo.rr + 1.25 * geo.m), style: { stroke: C.ring, strokeWidth: 2.5 } })),
  h('g', { key: 'sun', ref: sunRef, transform: `translate(${CX} ${CY})` },
    h('path', { d: geo.sun, style: { fill: C.sun, fillOpacity: 0.3, stroke: C.sun, strokeWidth: 1.2 } }),
    h('line', { x1: 0, y1: 0, x2: 0, y2: -geo.rs * 0.8, style: { stroke: C.sun, strokeWidth: 2.5 } })),
  txt('sl', CX, CY + 4, 'S', { anchor: 'middle', size: 12, weight: 700, color: C.sun, halo: true }),
  txt('rl', CX + (R_OUTER - 1.6 * geo.m) * Math.cos(-Math.PI / 4), CY + (R_OUTER - 1.6 * geo.m) * Math.sin(-Math.PI / 4) + 4, 'R', { anchor: 'middle', size: 12, weight: 700, color: C.ring, halo: true }),
  h('text', { key: 'cl', ref: cLabelRef, x: CX, y: CY, textAnchor: 'middle', fontSize: 12, fontWeight: 700, fontFamily: 'JetBrains Mono, ui-monospace, monospace', style: { fill: C.carrier, paintOrder: 'stroke', stroke: C.card, strokeWidth: 3 } }, 'C'));

  const items = [
    { key: 'sun', color: C.sun, name: generalLabels ? 'Sun (S)' : 'Sun (S) · MG1', teeth: `Zs ${zs}`, rpm: speeds.sunRpm },
    { key: 'carrier', color: C.carrier, name: generalLabels ? 'Carrier (C)' : 'Carrier (C) · Engine', teeth: `${n} planets`, rpm: speeds.carrierRpm },
    { key: 'ring', color: C.ring, name: generalLabels ? 'Ring (R)' : 'Ring (R) · Output + MG2', teeth: `Zr ${zr}`, rpm: speeds.ringRpm },
    { key: 'planet', color: C.muted, name: 'Planet pinion (P)', teeth: `Zp ${zp}`, rpm: pinionRelRpm, rel: true },
  ];
  return (
    <div className="space-y-3">
      {svg}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="ps-cutaway-legend">
        {items.map((it) => (
          <li key={it.key} className="flex items-baseline gap-2 min-w-0">
            <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0 translate-y-0.5" style={{ background: it.color }} />
            <span className="font-semibold">{it.name}</span>
            <span className="text-muted-foreground font-mono">{it.teeth}</span>
            <span className="ml-auto font-mono whitespace-nowrap" data-testid={`ps-cutaway-rpm-${it.key}`}>
              {fmtRpm(it.rpm)}{it.rel ? ' rel.' : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PlanetaryCutaway;
